import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { AxiosInstance } from 'axios';
import { setUnauthorizedHandler } from '../services/apiClient';
import { API_BASE_URL } from '../config';


type AuthContextValue = {
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, inviteToken: string) => Promise<void>;
  logout: () => void;
};

type AuthProviderProps = {
  children: React.ReactNode;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function extractToken(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const maybeData = data as { data?: { accessToken?: string }; accessToken?: string };
  return maybeData.data?.accessToken || maybeData.accessToken || null;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [token, setToken] = useState<string | null>(null);
  const navigate = useNavigate();

  const api: AxiosInstance = useMemo(() => {
    const authBaseUrl = `${API_BASE_URL}/auth`

    return axios.create({
      baseURL: authBaseUrl,
      withCredentials: true,
    });
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('jwt');
    if (stored) setToken(stored);
  }, []);

  const login = async (email: string, password: string) => {
    const resp = await api.post('/login', { email, password });
    const jwt = extractToken(resp.data);

    if (!jwt) throw new Error('Login response missing access token.');

    localStorage.setItem('jwt', jwt);
    setToken(jwt);
    navigate('/', { replace: true });
  };

  const register = async (email: string, password: string, inviteToken: string) => {
    const resp = await api.post(
      '/register',
      { email, password, token: inviteToken },
      { params: { token: inviteToken } }
    );

    const jwt = extractToken(resp.data);
    if (jwt) {
      localStorage.setItem('jwt', jwt);
      setToken(jwt);
      navigate('/', { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('jwt');
    setToken(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    setUnauthorizedHandler(() => logout());
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  return (
    <AuthContext.Provider value={{ token, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
